// The spike's save schema (gate A4.1): which slices a save holds, their versions and the migrations between them.
// Today one slice, `sim`: the whole deterministic sim (src/sim/host.ts SimSave: the kernel's module slices, the clock
// and RNG streams, the injected input, Rapier's world, the camera rig's snapshot when the scene has one).
//
//   sim v1  A1's save as the sim wrote it (`green-valley-sim@1`): Rapier's snapshot as base64 text inside the JSON.
//   sim v2  the same, with Rapier's snapshot as raw bytes: the container keeps it beside the JSON, where it isn't a
//           third bigger and compresses better (measured in the notes).
//   sim v3  A2's sim save (`green-valley-sim@2`): a `scene` field (a save restores only into its own scene; A1 had
//           only the lab), the one kernel game's module list with the core module first (the controller stream), an
//           optional camera snapshot (the camera stream); Rapier's snapshot as bytes.
//   sim v4  phase B: the valley has a fight (src/enemies/fight.ts): the "fight" and "bodies" modules join its kernel
//           game, with combat, the enemies, the tokens, the director, the lock and the bodies' poses and ragdolls in
//           their slices. A valley save from before comes in with both slices fresh (a fight not yet begun: the
//           camps are placed and the hero's hurt sensor made on its first step), so it loads and plays on; the
//           collision world gives the old snapshot's colliders the current layer table (the fight's layers were
//           added at its end). Other scenes' saves are unchanged.
//   sim v5  phase C joined: the streamed valley (scene "world") is the default scene, its kernel game the world's
//           residency and nav slicer, the sky's clock and weather, the interiors director and the things beside the
//           valley's machinery and the fight. Saves of every other scene carry on unchanged (A1's lab, phase A's and
//           B's test valley, now at `?scene=valley`). A streamed-valley save from before the join (its scene was born
//           in phase C and never shipped: its saves exist only in test runs) has no sky, rooms or things, and its
//           physics has no breakables in it, so it can't be carried on: it is refused with a reason.
//   sim v6  phase D joined: the streamed valley's kernel game gains the rpg layer, the town's townsfolk and cutscenes,
//           the story layer and discovery. A streamed-valley save from phase C carries on: each of those modules gets
//           a fresh slice (a new game's: no experience, the starting kit, nothing discovered, no quest taken), the
//           world, the fight and the things as they were (fixtures/c-world.json, made with phase C's code at
//           3dd5821). Every other scene's save is unchanged.
//
// A1 wrote bare `green-valley-sim@1` documents and A2's harness fixtures are bare `green-valley-sim@2` ones; the
// legacy hook lifts either into a document (v1 or v3), and the migrations carry v1 on. The old-save fixture
// (fixtures/a1-pushed.json) is A1's, frozen.
import { fromBase64, toBase64 } from "@rpg/collision";
import { defineSaveSchema, SAVE_DOC_FORMAT, type MigrationSteps, type SaveDoc } from "@rpg/save";
import { SIM_SAVE_FORMAT, type SimSave } from "../sim/host";
import { FRESH_SLICE } from "./fresh";

/** A1's bare sim save format (sim v1 and v2). */
export const A1_SIM_FORMAT = "green-valley-sim@1" as const;

/** The game id a save document carries. */
export const SAVE_GAME_ID = "green-valley";

/** Planted defects for the save checks (`?faults=no-save-migration`). */
export const SAVE_FAULTS = {
  /** The sim slice's v1 → v2 migration is missing: the old-save fixture must fail to load. */
  noSaveMigration: false,
  /** The v6 migration leaves out the phase-D modules' fresh slices: a phase-C save loaded into a page that has played
   *  keeps that play's rpg layer, town and story (the fixture's restore check must go red). */
  phaseDSlicesKept: false,
};

/** The sim slice at v2: A1's save with Rapier's snapshot as bytes. */
export type SimSliceV2 = Omit<SimSave, "physics" | "format" | "scene"> & { format: typeof A1_SIM_FORMAT; physics: Omit<SimSave["physics"], "rapier"> & { rapier: Uint8Array } };
/** The sim slice at v3 and v4 (the current; v4 adds the valley's fight slices): A2's sim save with Rapier's snapshot
 *  as bytes. */
export type SimSliceV3 = Omit<SimSave, "physics"> & { physics: Omit<SimSave["physics"], "rapier"> & { rapier: Uint8Array } };

/** v1 → v2: Rapier's base64 snapshot to bytes. */
export function simV1toV2(d: unknown): SimSliceV2 {
  const s = d as { format?: string; physics?: { rapier?: unknown } };
  if (s?.format !== A1_SIM_FORMAT || typeof s.physics?.rapier !== "string") throw new Error(`sim slice v1: not a ${A1_SIM_FORMAT} save`);
  const v1 = d as SimSliceV2 & { physics: { rapier: string } };
  return { ...v1, physics: { ...v1.physics, rapier: fromBase64(v1.physics.rapier) } };
}

/** v2 → v3: A1 had one scene, the lab; its kernel game had no core module (A2 put the core first in the list). */
export function simV2toV3(d: unknown): SimSliceV3 {
  const s = d as SimSliceV2;
  if (s?.format !== A1_SIM_FORMAT || !(s.physics?.rapier instanceof Uint8Array)) throw new Error("sim slice v2: not an A1 save with Rapier's bytes");
  const comp = s.kernel.composition as { modules?: { id: string; version: string }[] } | undefined;
  const modules = comp?.modules ?? [];
  const kernel = modules.some((m) => m.id === "core") ? s.kernel : { ...s.kernel, composition: { ...comp, modules: [{ id: "core", version: "0.1.0" }, ...modules] } };
  return { ...s, format: SIM_SAVE_FORMAT, scene: "lab", kernel } as SimSliceV3;
}

/** The fight's and the bodies' modules as phase B's valley composes them. */
export const FIGHT_MODULES = [
  { id: "fight", version: "0.1.0" },
  { id: "bodies", version: "0.1.0" },
] as const;

/** v3 → v4: a valley save gains the fight's and the bodies' slices, fresh (a fight not yet begun). */
export function simV3toV4(d: unknown): SimSliceV3 {
  const s = d as SimSliceV3;
  if (!s?.kernel || !(s.physics?.rapier instanceof Uint8Array)) throw new Error("sim slice v3: not a sim save with Rapier's bytes");
  if (s.scene !== "valley") return s;
  const comp = s.kernel.composition as { modules?: { id: string; version: string }[] } | undefined;
  const modules = comp?.modules ?? [];
  const add = FIGHT_MODULES.filter((m) => !modules.some((x) => x.id === m.id)).map((m) => ({ ...m }));
  const state = s.kernel.modules as Record<string, unknown>;
  return {
    ...s,
    kernel: {
      ...s.kernel,
      composition: { ...comp, modules: [...modules, ...add] },
      modules: { ...state, fight: state.fight ?? { fresh: true }, bodies: state.bodies ?? { t: 0, seq: 0, tracks: [], ragdolls: [] } },
    },
  };
}

/** The modules the streamed valley's kernel game gained when phase C was joined. */
export const WORLD_JOINED_MODULES = ["sky", "places", "things"] as const;

/** v4 → v5: every scene's save carries on unchanged, but a streamed-valley save from before phase C was joined. */
export function simV4toV5(d: unknown): SimSliceV3 {
  const s = d as SimSliceV3;
  if (!s?.kernel || !(s.physics?.rapier instanceof Uint8Array)) throw new Error("sim slice v4: not a sim save with Rapier's bytes");
  if (s.scene !== "world") return s;
  const modules = ((s.kernel.composition as { modules?: { id: string }[] } | undefined)?.modules ?? []).map((m) => m.id);
  const missing = WORLD_JOINED_MODULES.filter((id) => !modules.includes(id));
  if (missing.length) throw new Error(`sim slice v4: a streamed-valley save from before phase C was joined (no ${missing.join(", ")}): it can't be carried on; start a new game`);
  return s;
}

/** The modules the streamed valley's kernel game gained when phase D was joined (the rpg layer, the town's
 *  townsfolk and cutscenes, the story layer, discovery), in the order the scene composes them. */
export const PHASE_D_MODULES = [
  { id: "rpg", version: "0.1.0" },
  { id: "discovery", version: "0.1.0" },
  { id: "story", version: "0.1.0" },
  { id: "town", version: "0.1.0" },
  { id: "cutscenes", version: "0.1.0" },
] as const;

/** v5 → v6: a streamed-valley save from before phase D was joined carries on with every phase-D module's slice fresh
 *  (the `fresh` marker: a new game's rpg layer, town and story; src/save/fresh.ts); every other scene's save is
 *  unchanged. */
export function simV5toV6(d: unknown): SimSliceV3 {
  const s = d as SimSliceV3;
  if (!s?.kernel || !(s.physics?.rapier instanceof Uint8Array)) throw new Error("sim slice v5: not a sim save with Rapier's bytes");
  if (s.scene !== "world") return s;
  const comp = s.kernel.composition as { modules?: { id: string; version: string }[] } | undefined;
  const modules = comp?.modules ?? [];
  const add = PHASE_D_MODULES.filter((m) => !modules.some((x) => x.id === m.id)).map((m) => ({ ...m }));
  if (!add.length || SAVE_FAULTS.phaseDSlicesKept) return s;
  const state = { ...(s.kernel.modules as Record<string, unknown>) };
  for (const m of add) state[m.id] = { ...FRESH_SLICE };
  return { ...s, kernel: { ...s.kernel, composition: { ...comp, modules: [...modules, ...add] }, modules: state } };
}

/** The slice's data for a live sim save. */
export function toSimSlice(s: SimSave): SimSliceV3 {
  if (s?.format !== SIM_SAVE_FORMAT || typeof s.physics?.rapier !== "string") throw new Error(`sim slice: not a ${SIM_SAVE_FORMAT} save`);
  return { ...s, physics: { ...s.physics, rapier: fromBase64(s.physics.rapier) } };
}

/** The sim save a v3 slice holds, as the sim's restore takes it. */
export function fromSimSlice(d: SimSliceV3): SimSave {
  if (!(d?.physics?.rapier instanceof Uint8Array)) throw new Error("sim slice v3: Rapier's snapshot is not bytes");
  return { ...d, physics: { ...d.physics, rapier: toBase64(d.physics.rapier) } };
}

/** The sim slice's migrations; the planted defect hides the v2 step, as if it had been removed. */
const SIM_MIGRATIONS: MigrationSteps = Object.defineProperty({ 3: simV2toV3, 4: simV3toV4, 5: simV4toV5, 6: simV5toV6 }, 2, {
  enumerable: true,
  get: () => (SAVE_FAULTS.noSaveMigration ? undefined : simV1toV2),
});

/** A save from before documents: A1's bare sim save (v1), or a bare A2 one (v3, the harness's fixtures; the v4
 *  migration carries a valley one on). */
function legacy(raw: unknown): SaveDoc | null {
  const s = raw as Partial<SimSave> | { format?: string; step?: number } | null;
  if (s?.format === A1_SIM_FORMAT)
    return { format: SAVE_DOC_FORMAT, game: SAVE_GAME_ID, savedAt: 0, slices: { sim: { v: 1, data: raw } }, meta: { step: s.step ?? 0, legacy: "a1" } };
  if (s?.format === SIM_SAVE_FORMAT)
    return { format: SAVE_DOC_FORMAT, game: SAVE_GAME_ID, savedAt: 0, slices: { sim: { v: 3, data: toSimSlice(raw as SimSave) } }, meta: { step: s.step ?? 0, legacy: "a2" } };
  return null;
}

/** The spike's schema. */
export const SAVE_SCHEMA = defineSaveSchema({
  game: SAVE_GAME_ID,
  slices: { sim: { version: 6, migrations: SIM_MIGRATIONS } },
  legacy,
});
